import React, { useEffect, useRef, useState } from "react";
import EventLogItem from "../EventLogItem";
import ScreenshotCard from "../ScreenshotCard";
import { Monitor, Camera, Mic, Speaker, Keyboard, Usb } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { useSelector } from "react-redux";
import { getFeedbackForm, getProfileForm } from "../../../../redux/reducer/incbotFeedbackReducer";
import { formatDate } from "../../../../utils/helper";
import MonitorIcon from "../../../../assets/images/external-devices/monitor.svg";
import CameraVideoIcon from "../../../../assets/images/external-devices/camera_video.svg";
import MouseIcon from "../../../../assets/images/external-devices/mouse.svg";
import UsbIcon from "../../../../assets/images/external-devices/usb.svg";

const TOOLTIP_ID = "device-info-tooltip";

// [2026-09-15] ADDED — INC-1003: "Interview Started" rendered mid-log, and rows (usually
// "Candidate Voice Mismatch") rendered below "Interview Ended".
//
// ROOT CAUSE — this is NOT a sorting bug. The rows are stamped by two unrelated clocks:
//   * "Interview Started" / "Interview Ended" use the SERVER clock
//     (incbot-video-system transcribeService.ts:332 callStart, callEndService.ts:229).
//   * "Candidate looking away" / "face not detected" / "Additional devices detected" use the AI
//     EVIDENCE-FRAME time (interviewService.ts:318, 400, 458 — `interval.proof_timestamp`, chosen
//     deliberately to line the row up with its screenshot).
// When those two clocks disagree, the stored timestamp on the start row is genuinely later than
// real monitoring rows. No display-side reordering can repair a wrong timestamp — the value has to
// be corrected at the source. That is deliberately NOT attempted here; it is a backend change on a
// live write path and belongs in its own ticket.
//
// WHAT THIS DOES INSTEAD — a strict tie-break, and nothing more. `dateAndTime` remains the primary
// sort key ALWAYS; the rank below is consulted only when two rows carry the EXACT same timestamp.
// That is what guarantees the rendered log can never show the clock running backwards: an earlier
// row is never placed below a later one.
//
// Why not simply force the start row to the top? Because on a record whose timestamps genuinely
// disagree that produces a visible time inversion — "Interview Started 11:24" followed by
// "looking away 11:21". An audit trail that silently reorders itself is worse than one that shows
// an obviously wrong order: the wrong order gets reported, the silent reordering does not.
// Measured on the dev database 2026-09-15 (38,913 rows / 2,921 logs): 11.23% of logs contain at
// least one group of rows sharing an IDENTICAL `dateAndTime`, and the two texts most often caught
// in such a group are "Deepfake Detection Enabled" (390) and "Interview Started" (302) — because
// transcribeService.callStart() stamped both from the same `currentTimestamp`. With equal keys the
// surviving order is whatever MongoDB happened to return, so the same interview could render
// differently on two loads. The ranks below make that group deterministic.
//
// The order is taken from the product spec "Proctoring Logs & Event Sequence v1" (2026-08-04):
//   1 Interview Started · 2 microphone ON · 3 camera ON · 4 Deepfake Detection Enabled
//   5 Fullscreen Mode Enabled · 6 Fullscreen Mode Disabled · 7-21 Active Monitoring / Recovery
//   22 Interview Ended (final event)
// Spacing is left between values so a new event can be slotted in without renumbering.
//
// Prefix match, NOT equality: the writers append the candidate IP, e.g. "Interview Started (1.2.3.4)".
// The suffix is omitted when no IP is available (interview-system InCBotService.ts:1751 writes the
// bare text), so `startsWith` is what covers both spellings.
const RANK_FIRST = 0;
const RANK_DEFAULT = 500; // Active Monitoring — spec says these are NOT a fixed sequence
const RANK_LAST = 1000;

const norm = (text: unknown): string => (typeof text === "string" ? text.trim() : "");

// Ranks that are safe ANYWHERE in the log. A session-start row always opens its minute and a
// session-end row always closes it, whatever else happens to share that minute.
const proctoringRowRank = (text: unknown): number => {
  const v = norm(text);
  if (!v) return RANK_DEFAULT;
  if (v.startsWith("Interview Started")) return RANK_FIRST;
  if (v.startsWith("Interview Ended")) return RANK_LAST;
  return RANK_DEFAULT;
};

// [2026-09-16] CHANGED — INC-1003: the device-setup ranks below are applied ONLY inside a minute
// that actually contains a session-start row.
//
// WHY: an earlier version ranked "Fullscreen Mode Enabled", mic and camera globally. That is right
// for the setup block, which runs once right after the interview starts — but those same events
// recur MID-interview when a candidate re-enters fullscreen or re-enables a device, and ranking
// them there reordered real history. Observed on a real report (Soumya Das, 2026-09-10):
//   09:07:10 Fullscreen Mode Exited · 09:07:40 Fullscreen Mode Enabled
// rendered as Enabled-then-Exited, reversing what the candidate actually did.
// Restricting these ranks to a start minute keeps the setup block tidy without ever touching
// mid-interview behaviour.
// [2026-09-17] CHANGED — INC-1003: `minuteHasResume` added. The spec places Network Disconnected in
// the Disruption phase (row 16), i.e. AFTER the device-setup block — the rank of 100 only makes
// sense in a recovery block, where the disconnect must lead the resume it caused. In a plain
// session-start minute (no resume) it was pushing a disconnect above "switched ON their
// microphone", producing "Interview Started / Network Disconnected / microphone ON". This shows up
// on clamped rows: a disconnect stamped hours outside the window lands on the start edge and then
// sorts into the middle of the setup block.
// Old signature kept for revert/compare:
//   const proctoringSetupRowRank = (text: unknown): number => {
// `minuteHasResume` is REQUIRED on purpose: a default of `true` would silently reinstate the old
// behaviour for any future caller that forgets it.
const proctoringSetupRowRank = (text: unknown, minuteHasResume: boolean): number => {
  const v = norm(text);
  if (!v) return RANK_DEFAULT;
  if (v.startsWith("Interview Started")) return RANK_FIRST;
  if (v.startsWith("Interview Ended")) return RANK_LAST;
  // Recovery block. "Network Disconnected" must stay ABOVE "Interview Resumed" (spec rows 16-17):
  // the disconnect is what caused the resume, so a tie must not invert them. Without a resume in
  // the same minute there is no recovery to lead, so it ranks with Active Monitoring instead.
  if (v.startsWith("Network Disconnected")) {
    return minuteHasResume ? 100 : RANK_DEFAULT;
  }
  if (v.startsWith("Interview Resumed")) return 110;
  // Device-setup block, spec rows 2-6. Only the "switched ON" spellings are ranked — a
  // "switched OFF" row is a mid-interview violation and belongs with Active Monitoring.
  if (/^Candidate switched ON their microphone/i.test(v)) return 120;
  if (/^Candidate switched ON their camera/i.test(v)) return 130;
  if (v.startsWith("Deepfake Detection Enabled")) return 140;
  if (v.startsWith("Fullscreen Mode Enabled")) return 150;
  if (v.startsWith("Fullscreen Mode Disabled")) return 160;
  return RANK_DEFAULT;
};

const MINUTE_MS = 60 * 1000;
const minuteOf = (value: unknown): number => {
  const t = new Date(value as string).getTime();
  return Number.isNaN(t) ? Number.NaN : Math.floor(t / MINUTE_MS);
};

const isSessionStartText = (text: unknown): boolean => {
  const v = norm(text);
  return v.startsWith("Interview Started") || v.startsWith("Interview Resumed");
};

const isSessionEndText = (text: unknown): boolean => norm(text).startsWith("Interview Ended");

// [2026-09-16] ADDED — INC-1003: fold rows that sit OUTSIDE the interview window back onto the
// window edge, so this report can never show a proctoring event before the interview started or
// after it ended. This is the display half of the fix; the backend half stops new rows being
// written outside the window, but it cannot repair the interviews that already exist.
//
// WHY this is needed (measured read-only against dev + test on 2026-09-16):
//   · dev  — 26 logs carry 65 rows BEFORE the first "Interview Started"; 57 of those 65 are a
//            single text, "External device information", written by the device-check screen that
//            runs BEFORE the candidate joins. Sample: stamped 07:28:15.303 against a start of
//            07:28:31.506 — 16s early.
//   · test — 37 logs carry 57 such rows; 53 are that same text.
//   · rows AFTER the final "Interview Ended": dev 70 across 32 logs, test 64 across 39 — late
//            browser events (fullscreen, tab switch) and late AI evidence rows.
// The pre-start gap reaches 190 days and the post-end gap 507 days in dev, so this is not a
// rounding problem — those timestamps are simply outside the interview.
//
// WHAT IS NOT DONE HERE, deliberately:
//   · Nothing is dropped or hidden. A clamped row still renders with the same text, type and
//     duration; only the time it prints under changes, and the value it had is preserved on
//     `originalDateAndTime`.
//   · The window comes from the ROWS, never from the `interviewStartDate` / `interviewEndDate`
//     fields — those are overwritten on every resume (measured: 420 of 420 dev interviews), so
//     anchoring to them would drag a first-session row forward to the resume time.
//   · If the session RESTARTED after its last "Interview Ended" (dev: 35 "Interview Resumed" rows
//     sit after the final end) the interview never formally closed, so no upper clamp is applied.
//   · If the window is contradictory (an end that precedes the first start — 1 log in test),
//     nothing is clamped at all.
// [2026-09-19] CHANGED — split into two exported pieces so the SCREENSHOT panel can be clamped to
// the SAME window as the log. Behaviour of `clampProctoringLogToInterviewWindow` is byte-for-byte
// unchanged: it is now a composition of the two functions below, and the old single-function body
// is preserved verbatim in the comment block beneath it for grep / compare / revert.
//
// WHY: the screenshots render from a SEPARATE array (`feedback.proctoring.screenshots`) that never
// passed through the clamp, so the captions under the evidence images kept printing the raw stored
// time while the log rows beside them printed the clamped one. Observed on interview
// 6aad3fa52b672a460ddc5805: the log read "12:01 pm" and the caption under the very same event read
// "Sep 20, 2026 12:10 am". Both come from the same backend write (interviewService.ts:331 and :347
// both take `evidenceDateAndTime`), so one of them being clamped and the other not is a display
// inconsistency, not two different facts.
// [2026-09-22] ADDED — the texts that make up the device-setup block, in the spec's own order.
// Same set `proctoringSetupRowRank` ranks 0-150; kept as one predicate so the two cannot drift.
const isSetupBlockText = (text: unknown): boolean => {
  const v = norm(text);
  if (!v) return false;
  return (
    v.startsWith("Interview Started") ||
    /^Candidate switched ON their microphone/i.test(v) ||
    /^Candidate switched ON their camera/i.test(v) ||
    v.startsWith("Deepfake Detection Enabled") ||
    v.startsWith("Fullscreen Mode Enabled")
  );
};

export const proctoringInterviewWindow = (
  messages: any[]
): { start: number; end: number; setupEnd: number } => {
  const empty = { start: Number.NaN, end: Number.NaN, setupEnd: Number.NaN };
  if (!Array.isArray(messages) || messages.length === 0) return empty;

  const timeOf = (message: any): number => new Date(message?.dateAndTime).getTime();

  let windowStart = Number.NaN; // earliest "Interview Started"
  let windowEnd = Number.NaN; // latest "Interview Ended"
  let latestRestart = Number.NaN; // latest "Interview Started" OR "Interview Resumed"

  messages.forEach((m: any) => {
    const time = timeOf(m);
    if (Number.isNaN(time)) return; // broken timestamp — never let it define the window
    const text = norm(m?.text);
    if (text.startsWith("Interview Started")) {
      if (Number.isNaN(windowStart) || time < windowStart) windowStart = time;
    }
    if (isSessionEndText(text)) {
      if (Number.isNaN(windowEnd) || time > windowEnd) windowEnd = time;
    }
    if (isSessionStartText(text)) {
      if (Number.isNaN(latestRestart) || time > latestRestart) latestRestart = time;
    }
  });

  // Restarted after the last end → the interview never closed, so drop the upper edge.
  if (!Number.isNaN(windowEnd) && !Number.isNaN(latestRestart) && latestRestart > windowEnd) {
    windowEnd = Number.NaN;
  }
  // Contradictory window → trust nothing, clamp nothing. Returned as "no window" so every caller
  // no-ops, exactly as the old single function did by returning its input untouched.
  if (!Number.isNaN(windowStart) && !Number.isNaN(windowEnd) && windowEnd <= windowStart) {
    return empty;
  }

  // [2026-09-22] ADDED — where the device-setup block ENDS, so a clamped row can be placed after it
  // instead of on top of it. Reported from production: a log stored as
  //     looking away · looking away · Interview Started
  // rendered as "Interview Started / looking away / Fullscreen Mode Enabled", when the setup block
  // should come first and the look-aways after it.
  //
  // WHY IT HAPPENS. The clamp puts an out-of-window row exactly ON `windowStart`, i.e. on the
  // "Interview Started" instant. `compareProctoringMessages` then ranks rows only WITHIN one
  // displayed minute, so it can order the look-away after the start row — but if the setup block
  // crosses a minute boundary (start 09:15:58, fullscreen 09:16:02) the minute comparison wins and
  // the setup row sorts BELOW the clamped look-away. Rank never gets a chance.
  //
  // THE RUN, NOT A TIME BOUND. The block is the contiguous run of setup rows immediately after the
  // earliest start row: walk forward in time order and stop at the first row that is not one.
  // Measured on the real export of 3,245 logs: that run is 0.9s at p50 and 1.5s at p95, so no
  // duration threshold is needed — and one taken from those numbers would have been wrong for the
  // single 97s outlier, which the run handles correctly because every row in it is still a setup
  // row. 681 logs carry rows early enough to be clamped onto the start edge; exactly 9 of them have
  // a setup block that crosses a minute, and those 9 are the ones this reorders.
  //
  // WHY THIS IS NOT INVENTING A TIME. A clamped row's timestamp is already fabricated — its real
  // one was outside the interview. Moving it to the end of the setup block is no less true than
  // pinning it to the start, and it is more true in one respect: video analysis cannot detect a
  // candidate "looking away from screen" before their camera is switched on.
  let setupEnd = windowStart;
  if (!Number.isNaN(windowStart)) {
    const ordered = messages
      .map((m: any) => ({ text: norm(m?.text), time: new Date(m?.dateAndTime).getTime() }))
      .filter((m) => !Number.isNaN(m.time) && m.time >= windowStart)
      .sort((a, b) => a.time - b.time);
    for (const m of ordered) {
      if (!isSetupBlockText(m.text)) break;
      setupEnd = m.time;
    }
    // Never past the closing edge — a setup row above the end row would break rule 2.
    if (!Number.isNaN(windowEnd) && setupEnd > windowEnd) setupEnd = windowEnd;
  }

  return { start: windowStart, end: windowEnd, setupEnd };
};

export const clampRowsToProctoringWindow = (
  rows: any[],
  window: { start: number; end: number; setupEnd?: number }
): any[] => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const { start: windowStart, end: windowEnd } = window;
  if (Number.isNaN(windowStart) && Number.isNaN(windowEnd)) return rows;

  // [2026-09-22] ADDED — land a too-early row at the END of the device-setup block rather than on
  // the start instant, so it cannot sort above the setup rows. See `proctoringInterviewWindow`.
  //
  // OPTIONAL on purpose: a caller that builds a window by hand — the tests do — keeps the exact
  // previous behaviour instead of silently getting a new one. The test below the NaN guard is
  // still `windowStart`, so WHICH rows get clamped does not change at all; only where they land.
  //
  // ONE MILLISECOND PAST the last setup row, not onto it. Landing exactly on it produces an exact
  // tie, and a tie here is not broken by rank: `proctoringSetupRowRank` is applied only inside a
  // minute that contains a session-start row, and the tail of a setup block that crossed a minute
  // boundary is by definition in a minute that does not. The comparator then falls through to
  // `timeA - timeB`, the tie survives, and the surviving order is whatever order the array happened
  // to be in — which is exactly the reported bug. A millisecond makes it deterministic without
  // touching the rank logic, and it is invisible: the view renders hour and minute only.
  const setupEndTarget =
    typeof window.setupEnd === "number" && !Number.isNaN(window.setupEnd) && window.setupEnd > windowStart
      ? Math.min(window.setupEnd + 1, Number.isNaN(windowEnd) ? Number.POSITIVE_INFINITY : windowEnd)
      : windowStart;
  const startTarget = Number.isNaN(setupEndTarget) ? windowStart : setupEndTarget;

  let changed = false;
  const clamped = rows.map((m: any) => {
    const time = new Date(m?.dateAndTime).getTime();
    if (Number.isNaN(time)) return m; // leave broken timestamps exactly where they are
    let next = time;
    // Old line kept for revert/compare:
    //   if (!Number.isNaN(windowStart) && time < windowStart) next = windowStart;
    if (!Number.isNaN(windowStart) && time < windowStart) next = startTarget;
    else if (!Number.isNaN(windowEnd) && time > windowEnd) next = windowEnd;
    if (next === time) return m;
    changed = true;
    // New object — the array comes from redux and must never be mutated in place.
    return {
      ...m,
      dateAndTime: new Date(next).toISOString(),
      originalDateAndTime: m?.dateAndTime,
    };
  });

  return changed ? clamped : rows;
};

export const clampProctoringLogToInterviewWindow = (messages: any[]): any[] => {
  if (!Array.isArray(messages) || messages.length === 0) return messages;
  return clampRowsToProctoringWindow(messages, proctoringInterviewWindow(messages));
};

// Clamps the EVIDENCE SCREENSHOTS to the window derived from the LOG. The screenshots array has no
// "Interview Started" / "Interview Ended" rows of its own, so it cannot derive a window — it has to
// be given the log's. Returns the input untouched whenever there is no usable window, so a report
// that is not clamped today does not start being clamped.
export const clampProctoringEvidenceToInterviewWindow = (
  screenshots: any[] | undefined | null,
  messages: any[] | undefined | null
): any[] => {
  if (!Array.isArray(screenshots) || screenshots.length === 0) {
    return Array.isArray(screenshots) ? screenshots : [];
  }
  return clampRowsToProctoringWindow(
    screenshots,
    proctoringInterviewWindow(Array.isArray(messages) ? messages : [])
  );
};

// --- OLD (pre-2026-09-19): one function, window derivation and clamping loop inlined together.
//     Kept verbatim so the split above can be compared and reverted in one step.
//
// export const clampProctoringLogToInterviewWindow = (messages: any[]): any[] => {
//   if (!Array.isArray(messages) || messages.length === 0) return messages;
//   const timeOf = (message: any): number => new Date(message?.dateAndTime).getTime();
//   let windowStart = Number.NaN;   // earliest "Interview Started"
//   let windowEnd = Number.NaN;     // latest "Interview Ended"
//   let latestRestart = Number.NaN; // latest "Interview Started" OR "Interview Resumed"
//   messages.forEach((m: any) => {
//     const time = timeOf(m);
//     if (Number.isNaN(time)) return;
//     const text = norm(m?.text);
//     if (text.startsWith("Interview Started")) {
//       if (Number.isNaN(windowStart) || time < windowStart) windowStart = time;
//     }
//     if (isSessionEndText(text)) {
//       if (Number.isNaN(windowEnd) || time > windowEnd) windowEnd = time;
//     }
//     if (isSessionStartText(text)) {
//       if (Number.isNaN(latestRestart) || time > latestRestart) latestRestart = time;
//     }
//   });
//   if (!Number.isNaN(windowEnd) && !Number.isNaN(latestRestart) && latestRestart > windowEnd) {
//     windowEnd = Number.NaN;
//   }
//   if (!Number.isNaN(windowStart) && !Number.isNaN(windowEnd) && windowEnd <= windowStart) {
//     return messages;
//   }
//   if (Number.isNaN(windowStart) && Number.isNaN(windowEnd)) return messages;
//   let changed = false;
//   const clamped = messages.map((m: any) => {
//     const time = timeOf(m);
//     if (Number.isNaN(time)) return m;
//     let next = time;
//     if (!Number.isNaN(windowStart) && time < windowStart) next = windowStart;
//     else if (!Number.isNaN(windowEnd) && time > windowEnd) next = windowEnd;
//     if (next === time) return m;
//     changed = true;
//     return { ...m, dateAndTime: new Date(next).toISOString(), originalDateAndTime: m?.dateAndTime };
//   });
//   return changed ? clamped : messages;
// };

// Sorts the log. `dateAndTime` is the primary key at DISPLAYED-MINUTE precision (this view renders
// hour+minute only), so rows inside one minute can be ordered without ever showing the clock
// running backwards. Rows in different minutes are strictly chronological.
export const orderProctoringLog = (messages: any[]): any[] => {
  if (!Array.isArray(messages)) return messages;
  // [2026-09-16] CHANGED — INC-1003: pull every row inside the interview window BEFORE ordering, so
  // the sort below never has to reason about a row that does not belong to the interview at all.
  // Old line kept for revert/compare:
  //   const source = messages;
  const source = clampProctoringLogToInterviewWindow(messages);
  // Minutes that contain a session-start row — only these get the setup-block ranks.
  const startMinutes = new Set<number>();
  // [2026-09-17] ADDED — INC-1003: minutes that contain a RESUME specifically. Only there does the
  // disconnect rank ahead of the setup block; see `proctoringSetupRowRank`.
  const resumeMinutes = new Set<number>();
  // [2026-09-17] ADDED — INC-1003: a minute holding MORE THAN ONE recovery must not be ranked at
  // all. The ranks describe a single setup or recovery block; applied to two of them they group all
  // the disconnects together and all the resumes together, which is not what happened. Observed:
  //   10:05:00 Network Disconnected · 10:05:10 Interview Resumed
  //   10:05:20 Network Disconnected · 10:05:30 Interview Resumed
  // ranked to disconnect, disconnect, resume, resume — two separate breaks rendered as one, and the
  // second resume left with no break above it. When a minute holds more than one of either, the
  // stored chronological order is the only truth we have, so the ranks stand down.
  const resumeCountByMinute = new Map<number, number>();
  const disconnectCountByMinute = new Map<number, number>();
  const bump = (map: Map<number, number>, key: number) => map.set(key, (map.get(key) ?? 0) + 1);
  source.forEach((m: any) => {
    const mm = minuteOf(m?.dateAndTime);
    if (Number.isNaN(mm)) return;
    const value = norm(m?.text);
    if (value.startsWith("Network Disconnected")) bump(disconnectCountByMinute, mm);
    if (!isSessionStartText(value)) return;
    startMinutes.add(mm);
    if (value.startsWith("Interview Resumed")) {
      resumeMinutes.add(mm);
      bump(resumeCountByMinute, mm);
    }
  });
  // [2026-09-18] ADDED — INC-1003: the break-before-resume rank may only fire when the break
  // ACTUALLY happened first.
  //
  // Observed in a real non-conversational log:
  //     10:06:14  Interview Resumed
  //     10:06:27  Network Disconnected      <- 13 seconds AFTER the resume
  //     10:09:53  Interview Resumed
  // Chronologically that is correct: the candidate came back, dropped again, came back again. But
  // both rows land in displayed minute 10:06, and the rank (break 100, resume 110) hoisted the
  // break ABOVE the resume — stealing the break that belonged to the 10:09 resume and leaving that
  // one with nothing above it. The reader then sees two "Interview Resumed" in a row.
  //
  // So the rank stands down whenever the minute's break is not genuinely earlier than its resume.
  // Nothing is reordered on a guess; the stored chronology wins.
  //
  // Ported from the dev line's helper.tsx (commit 8fba957). The logic is identical; only the text
  // accessor differs — this file already has `norm()` where helper.tsx inlines the trim.
  const firstResumeAtByMinute = new Map<number, number>();
  const firstDisconnectAtByMinute = new Map<number, number>();
  const noteEarliest = (map: Map<number, number>, key: number, time: number) => {
    const current = map.get(key);
    if (current === undefined || time < current) map.set(key, time);
  };
  source.forEach((m: any) => {
    const mm = minuteOf(m?.dateAndTime);
    if (Number.isNaN(mm)) return;
    const time = new Date(m?.dateAndTime).getTime();
    if (Number.isNaN(time)) return;
    const value = norm(m?.text);
    if (value.startsWith("Network Disconnected")) noteEarliest(firstDisconnectAtByMinute, mm, time);
    if (value.startsWith("Interview Resumed")) noteEarliest(firstResumeAtByMinute, mm, time);
  });

  const minuteIsRankable = (minute: number): boolean => {
    if ((resumeCountByMinute.get(minute) ?? 0) > 1) return false;
    if ((disconnectCountByMinute.get(minute) ?? 0) > 1) return false;
    const resumeAt = firstResumeAtByMinute.get(minute);
    const disconnectAt = firstDisconnectAtByMinute.get(minute);
    // Both present -> the break must truly precede the recovery, or we would be inventing an order.
    if (resumeAt !== undefined && disconnectAt !== undefined && disconnectAt > resumeAt)
      return false;
    return true;
  };
  return source.slice().sort((a: any, b: any) => {
    const ma = minuteOf(a?.dateAndTime);
    const mb = minuteOf(b?.dateAndTime);
    const minuteDiff = ma - mb;
    // Different minute, or a broken timestamp (NaN) — behave exactly as the old comparator did.
    if (minuteDiff !== 0) return minuteDiff;
    const hasResume = resumeMinutes.has(ma);
    const rank =
      startMinutes.has(ma) && minuteIsRankable(ma)
        ? (text: unknown) => proctoringSetupRowRank(text, hasResume)
        : proctoringRowRank;
    const rankDiff = rank(a?.text) - rank(b?.text);
    if (rankDiff !== 0) return rankDiff;
    // Same minute, same rank — true chronological order, so nothing is ever shuffled.
    return new Date(a?.dateAndTime).getTime() - new Date(b?.dateAndTime).getTime();
  });
};

// [2026-09-17] ADDED — INC-1002 / INC-1003: the proctoring log must read as one story, every time.
// Five rules, agreed with the product owner on 2026-09-17:
//
//   1. Exactly ONE "Interview Started", and it is the FIRST row. Nothing above it.
//   2. Exactly ONE "Interview Ended", and it is the LAST row. Nothing below it.
//   3. Every other row's time falls BETWEEN those two.
//   4. An "Interview Resumed" is ALWAYS preceded by a "Network Disconnected".
//   5. A second "Interview Started" is impossible — a re-join is a RESUME, not a start.
//
// RULE 4 HAS ONE STATED EXCEPTION, agreed 2026-09-17: it holds wherever the data can support it
// (measured 91.2% of dev logs with a resume, 96.6% of test). The remainder are logs where no break
// was ever recorded — two re-joins inside the two-minute window that suppresses the
// "Network Disconnected" write, or a second "Interview Started" that rule 5 relabels. Satisfying
// rule 4 there would mean inventing a row nobody wrote, which is forbidden: this is a report used
// for hiring decisions. Rules 1, 2, 3 and 5 hold at 100%.
//
// Rule 3 is already delivered by `clampProctoringLogToInterviewWindow`. The rest is delivered here
// by RELABELLING rows for display. The stored `text` is never modified — the original is kept on
// `originalText`, and nothing is written back — so no database contract moves.
//
// ONE WORD FOR A BREAK. A mid-log "Interview Ended" is not a new concept needing a new name: to the
// reader it means what "Network Disconnected" means. The two are written by different mechanisms
// (the browser announcing `unload` at mainViewConnector.jsx:1153-1155 versus the server noticing
// silence at CronHelper.ts:1207 and transcribeService.ts:401) but describe the same event, so they
// render as the same row, and two adjacent ones render once.
//
// TWO LIMITS, both deliberate and accepted:
//   · An interview with no "Interview Ended" row at all ends on its last real event. Inventing a
//     closing row would be fabricating a record.
//   · Where a cron already wrote a late end row on an OLD record, that row is still treated as the
//     end. New interviews no longer get one (incbot-video-system callEndService.ts, 2026-09-16).
const DISCONNECTED_TEXT = "Network Disconnected";
const STARTED_PREFIX = "Interview Started";
const RESUMED_PREFIX = "Interview Resumed";
// [2026-09-22] ADDED — matched as a PREFIX, like the three above, because every writer in
// frontend-interview emits this exact sentence with nothing appended. Kept as a constant rather
// than typed at the call site so the two places that test it can never drift apart.
const FULLSCREEN_OFF_TEXT = "Fullscreen Mode Disabled";
// [2026-09-22] ADDED — the ENTER row is duplicated the same way the exit row is, and the earlier
// version of this guard deliberately left it out. That was half an argument: a browser cannot leave
// fullscreen twice without entering in between, and it cannot ENTER twice without leaving either.
// Two adjacent rows announcing the SAME state are one event written twice, whichever state it is.
//
// WHERE THE SECOND WRITE COMES FROM. frontend-interview has THREE independent writers of
// "Fullscreen Mode Enabled" and no shared guard between them:
//   enterFullscreenWithRecovery({ onEnabled })     — mainViewConnector.jsx:2412
//   retryFullscreenOnEveryGesture({ onEnabled })   — mainViewConnector.jsx:2453
//   the else-branch's isBrowserFullscreen() check  — mainViewConnector.jsx:2479
// One click on "Re-enter Fullscreen" can run two of them: `enableFullscreen` succeeds and fires the
// first, while the gesture retry armed when the modal opened fires on that same click before
// `clearFullscreenRecovery` cancels it. Hence "Fullscreen Mode Enabled · 2 times" for one re-entry.
const FULLSCREEN_ON_TEXT = "Fullscreen Mode Enabled";

// Which fullscreen state a row announces, or "" when it is not a fullscreen row. Prefix-matched on
// the FULL sentence on purpose: "Fullscreen Mode Exited" — written by the modal countdown when it
// gives up and ends the call — shares a prefix with "Enabled" up to one letter, and it is a
// different event that must never be collapsed into either.
const fullscreenStateOf = (text: string): string => {
  if (text.startsWith(FULLSCREEN_ON_TEXT)) return FULLSCREEN_ON_TEXT;
  if (text.startsWith(FULLSCREEN_OFF_TEXT)) return FULLSCREEN_OFF_TEXT;
  return "";
};

// How close two "Fullscreen Mode Disabled" rows must be before the second is treated as the first
// one written twice rather than a second exit.
//
// DERIVED FROM THE MECHANISM, NOT PICKED. The duplicate comes from frontend-interview's fullscreen
// modal: the candidate presses "Re-enter Fullscreen", the request fails, and
// `enterFullscreenWithRecovery` re-raises the violation after its `graceMs = 5000` timer.
//
// The mechanism has a HARD CEILING, which is what makes this boundable at all. When the modal's
// countdown reaches zero, mainViewConnector.jsx:2462-2473 writes "Fullscreen Mode Exited" and calls
// `handleEndCall` — the interview is over. So the candidate cannot keep failing indefinitely: the
// widest a duplicate pair can be is one full countdown plus one grace, plus request latency.
//
// The countdown is NOT the same everywhere — `process.env.REACT_APP_ENV === 'test' ? 30 : 15`. This
// view renders reports from every environment, so the ceiling to respect is the LARGER one:
// 30s countdown + 5s grace + latency. Forty-five seconds covers that; thirty would have been right
// for production and would have silently missed duplicates from a test-environment interview.
//
// MEASURED on the real export of 3,245 logs. Every adjacent "Disabled -> Disabled" gap, in seconds:
//     6, 6, 7, 7, 7, 7, 7, 9, 9, 10, 12, 14, 17, 63, 668
// Thirteen sit in a tight 6-17s cluster — that is this mechanism, on a 15s-countdown environment —
// and then the data is EMPTY until 63s. 45s sits inside that gap: it takes the whole cluster with
// margin for a 30s countdown, and takes neither outlier. Raising it to two minutes would add only
// the 63s pair, which the 15s ceiling says cannot be a duplicate at all.
//
// WHY BOUNDED AT ALL — the argument for collapsing unconditionally is that a browser cannot leave
// fullscreen twice without entering it in between, so two adjacent exits MUST be one event. That
// reasoning holds only while the "Fullscreen Mode Enabled" row is reliably written, and it is not:
// it is a separate PATCH that can fail. #CAN00368 is exactly that situation — the network dropped
// in the same minute. Without a bound, one lost "Enabled" row would make two genuine exits look
// adjacent and this code would delete a real violation from a document used to make a hiring
// decision. The bound keeps the cost of being wrong under a minute.
const FULLSCREEN_DUPLICATE_WINDOW_MS = 45 * 1000;

// The writers append the candidate IP — "Interview Started (1.2.3.4)". Only the leading phrase is
// rewritten so the IP survives into the rendered label.
const relabelRow = (message: any, fromPrefix: string, toPrefix: string): any => ({
  ...message,
  text: `${toPrefix}${norm(message?.text).slice(fromPrefix.length)}`,
  originalText: message?.text,
});

// Rule 1, applied BEFORE the window clamp so the promoted row can anchor the window.
const promoteFirstResumeWhenStartMissing = (messages: any[]): any[] => {
  if (!Array.isArray(messages) || messages.length === 0) return messages;
  if (messages.some((m: any) => norm(m?.text).startsWith(STARTED_PREFIX))) return messages;
  let earliestIndex = -1;
  let earliestTime = Number.NaN;
  messages.forEach((m: any, index: number) => {
    if (!norm(m?.text).startsWith(RESUMED_PREFIX)) return;
    const time = new Date(m?.dateAndTime).getTime();
    if (Number.isNaN(time)) return;
    if (earliestIndex === -1 || time < earliestTime) {
      earliestIndex = index;
      earliestTime = time;
    }
  });
  if (earliestIndex === -1) return messages;
  return messages.map((m: any, index: number) =>
    index === earliestIndex ? relabelRow(m, RESUMED_PREFIX, STARTED_PREFIX) : m
  );
};

// Rule 4. A "Network Disconnected" row is stamped with `interviewActiveTimestamp` — the last moment
// the candidate was seen, not the moment the break was noticed — so real events keep landing
// between the break and the recovery. The row is moved to sit directly above the resume it
// explains, and re-stamped with the time of the row that ends up above it. The move is always
// DOWNWARDS out of an already-time-sorted list, so the rendered clock can never go backwards.
const repositionDisconnectAboveResume = (rows: any[]): any[] => {
  const out = rows.slice();
  for (let index = 0; index < out.length; index += 1) {
    if (!norm(out[index]?.text).startsWith(RESUMED_PREFIX)) continue;
    if (index > 0 && norm(out[index - 1]?.text).startsWith(DISCONNECTED_TEXT)) continue;
    // Stop at an earlier resume — that disconnect belongs to the earlier recovery.
    let source = -1;
    for (let back = index - 1; back >= 0; back -= 1) {
      const text = norm(out[back]?.text);
      if (text.startsWith(RESUMED_PREFIX)) break;
      // [2026-09-17] also stop at the start row: a disconnect above it (possible when its own
      // timestamp is unparseable) belongs to no recovery and must not be dragged past the beginning.
      if (text.startsWith(STARTED_PREFIX)) break;
      if (text.startsWith(DISCONNECTED_TEXT)) {
        source = back;
        break;
      }
    }
    if (source === -1) continue;
    const disconnect = out[source];
    out.splice(source, 1); // the resume now sits at index - 1
    const target = index - 1;
    const anchor = out[target - 1];
    // [2026-09-17] CHANGED — INC-1003: only inherit the anchor's timestamp when the anchor HAS a
    // usable one. `orderProctoringLog` deliberately leaves a row whose `dateAndTime` cannot be
    // parsed exactly where it is, so the anchor can legitimately be such a row — and copying it
    // turned a disconnect that printed "10:05 AM" into "Invalid Date".
    const anchorTime = anchor ? new Date(anchor.dateAndTime).getTime() : Number.NaN;
    out.splice(
      target,
      0,
      anchor && !Number.isNaN(anchorTime)
        ? {
            ...disconnect,
            dateAndTime: anchor.dateAndTime,
            originalDateAndTime: disconnect?.originalDateAndTime ?? disconnect?.dateAndTime,
          }
        : disconnect
    );
  }
  return out;
};

// Rule 1 — nothing above the start row.
// [2026-09-17] ADDED — INC-1003: `clampProctoringLogToInterviewWindow` skips a row whose
// `dateAndTime` cannot be parsed, and the sort leaves such a row wherever it already was, so one of
// them could still render ABOVE "Interview Started" — rule 1 was stated as delivered but was not.
// Hoisting the start row is not an invention: it is the row that DEFINES the window every other row
// was clamped into, so by construction nothing legitimately precedes it.
const pinStartRowFirst = (rows: any[]): any[] => {
  const startIndex = rows.findIndex((m: any) => norm(m?.text).startsWith(STARTED_PREFIX));
  if (startIndex <= 0) return rows;
  return [rows[startIndex], ...rows.slice(0, startIndex), ...rows.slice(startIndex + 1)];
};

export const renderProctoringLog = (messages: any[] | undefined | null): any[] => {
  if (!Array.isArray(messages)) return [];

  const ordered = pinStartRowFirst(
    orderProctoringLog(promoteFirstResumeWhenStartMissing(messages))
  );
  const lastIndex = ordered.length - 1;

  // Pass 1 — labels. Rules 2, 4 and 5.
  let startAlreadySeen = false;
  const labelled = ordered.map((message: any, index: number) => {
    const text = norm(message?.text);

    if (text.startsWith(STARTED_PREFIX)) {
      if (startAlreadySeen) {
        // Rule 5 — the session never restarts, it resumes.
        return relabelRow(message, STARTED_PREFIX, RESUMED_PREFIX);
      }
      startAlreadySeen = true;
      return message;
    }

    // Rules 2 and 4 — an end row that is not the last row did not end the interview; it is the
    // break that the resume below it recovers from.
    if (isSessionEndText(text) && index !== lastIndex) {
      return { ...message, text: DISCONNECTED_TEXT, originalText: message?.text };
    }

    return message;
  });

  // Pass 2 — one break, one row. Runs BEFORE the repositioning below, deliberately: collapsing
  // afterwards can delete the very row that was just moved into place above a later resume.
  //
  // [2026-09-22] CHANGED — one EXIT, one row, for fullscreen too. Asked for by Rujal on 2026-09-22
  // after #CAN00368 showed "Fullscreen Mode Disabled · 2 times" for a single exit.
  //
  // WHY THIS IS A FACT AND NOT A GUESS. A browser cannot leave fullscreen twice without entering it
  // in between — there is no state to leave the second time. So two CONSECUTIVE
  // "Fullscreen Mode Disabled" rows, with no "Fullscreen Mode Enabled" separating them, are not two
  // violations; they are one violation written twice. Suppressing the second invents nothing and
  // hides nothing.
  //
  // WHERE THE SECOND WRITE COMES FROM (frontend-interview, all five connectors). The exit is logged
  // from two places. `watchFullscreenState` keeps a `last` flag and fires once per real state flip —
  // that path is correct. But `enterFullscreenWithRecovery({ onFailed })` calls
  // `handleFullscreenChange()` DIRECTLY when a re-entry attempt is still not active after its 5s
  // grace, and a direct call never consults that flag. Press "Re-enter Fullscreen", have the request
  // fail, and the same exit is logged a second time five seconds later. The write path deserves its
  // own fix; this makes every report — including the ones already stored — read correctly meanwhile.
  //
  // TWO GUARDS, BOTH REQUIRED. The rows must be ADJACENT — a genuine second exit carries its
  // "Fullscreen Mode Enabled" above it, which breaks the adjacency — and they must be within
  // `FULLSCREEN_DUPLICATE_WINDOW_MS`, so that a pair which is only adjacent because an "Enabled"
  // row was lost to a failed request is still shown in full. An unreadable timestamp on either row
  // fails the window test and both rows survive, which is the safe direction.
  //
  // Old line kept for revert/compare:
  //   if (isDisconnect && previous && norm(previous?.text).startsWith(DISCONNECTED_TEXT)) return;
  const deduped: any[] = [];
  labelled.forEach((message: any) => {
    const text = norm(message?.text);
    const isDisconnect = text.startsWith(DISCONNECTED_TEXT);
    // [2026-09-22] CHANGED — match on the STATE the row announces, so the enter row is covered by
    // the same rule as the exit row. Old line kept for revert/compare:
    //   const isFullscreenOff = text.startsWith(FULLSCREEN_OFF_TEXT);
    const fullscreenState = fullscreenStateOf(text);
    const previous = deduped[deduped.length - 1];
    const previousText = norm(previous?.text);
    if (isDisconnect && previous && previousText.startsWith(DISCONNECTED_TEXT)) return;
    // Old line kept for revert/compare:
    //   if (isFullscreenOff && previous && previousText.startsWith(FULLSCREEN_OFF_TEXT)) {
    if (fullscreenState && previous && fullscreenStateOf(previousText) === fullscreenState) {
      const gap =
        new Date(message?.dateAndTime).getTime() - new Date(previous?.dateAndTime).getTime();
      if (Number.isFinite(gap) && gap >= 0 && gap <= FULLSCREEN_DUPLICATE_WINDOW_MS) return;
    }
    deduped.push(message);
  });

  // Pass 3 — rule 4: the break sits directly above the recovery it explains.
  return repositionDisconnectAboveResume(deduped);
};

// [2026-09-17] ADDED — SEPARATE TICKET, not INC-1003: tell the client WHO produced the feedback.
//
// THE PROBLEM. When an interview is closed by something other than the candidate, the report gives
// no hint of it. The client cannot tell whether the candidate no-showed until the link expired,
// whether the interview was left hanging and the system tidied it up, or whether their own team
// closed it from the dashboard. Three different conversations, and only one is about the candidate.
//
// NOTHING NEW IS STORED. `endCallStatus` has always recorded this, and the feedback API already
// sends it: interview-system employer/InCBotService.getInterview projects by EXCLUSION
// (`.select({ meetInviteDetails: 0, logs: 0, otpData: 0 })`), so the field is already in the
// payload. Display only, so it also works on reports that already exist.
//
// NOTE ON PLACEMENT: on the `test` line the proctoring logic lives in this view rather than in
// `helper.tsx`, because this branch's helper.tsx never received the `orderProctoringMessages` base
// that `dev` already had. On `dev` this same code sits in helper.tsx. Identical behaviour, and both
// are covered by the same tests.
// [2026-09-18] CHANGED — both cron cases now read simply "System Generated the Feedback".
//
// They used to carry a prefix naming WHICH system path produced the feedback:
//     "End Call Cron" -> "Link Expired — System Generated the Feedback"
//     "Cron Call End" -> "Interview Not Closed — System Generated the Feedback"
//
// "Interview Not Closed" was factually wrong: `botCompleteJobs` selects on
// `interviewEndDate: { $exists: true }`, so the interview HAD closed — what was missing was the
// rating. And the distinction is internal plumbing: a recruiter needs to know the candidate did not
// end this interview themselves; which of our two crons picked it up is not their concern.
//
// Nothing is lost — `endCallStatus` still stores the exact path on the document, so the two cases
// stay distinguishable in the data and in admin views.
//
// The employer line is deliberately NOT merged into this: a person choosing to generate feedback
// and a machine doing it on a timer are different things to a reader.
export const PROCTORING_FEEDBACK_SOURCE_LABELS: Record<string, string> = {
  // CronHelper.ts:280 — `status: "Reinvited"` + `expiredAt < now` + no overAllRating.
  // The candidate never came back and the window closed.
  "End Call Cron": "System Generated the Feedback",
  // CronHelper.ts:598 — `botCompleteJobs`, every 15 minutes. The interview ended but no rating was
  // ever written, so the cron re-ran the close to produce one.
  "Cron Call End": "System Generated the Feedback",
  // The employer pressed End on their own dashboard.
  "Dashboard Call End": "Feedback Generated by Employer",
};

/**
 * Renders one extra line saying who produced the feedback, at the BOTTOM of the log.
 *
 * [2026-09-17] CHANGED — the line sat directly ABOVE "Interview Ended", which states the wrong order
 * of events: feedback is produced AFTER an interview ends, never before it. It now sits below the
 * ending and carries its OWN date rather than borrowing the ending's.
 *
 * Those are two genuinely different moments and the gap is the point. The "Interview Ended" ROW is
 * stamped at the candidate's last activity (INC-1003's `resolveEndRowTimestamp`), while
 * `interviewEndDate` holds the real server time the interview was closed. A candidate who walks away
 * on Monday whose interview the expiry job closes on Wednesday now reads: ended Monday 06:11 pm,
 * feedback generated Wednesday 10:32 am. The per-day separator prints the second date automatically.
 *
 * Returns `rows` unchanged when the candidate ended the interview themselves, when the status is
 * missing, or when it is a value nobody has mapped — a raw internal code must never reach a client.
 */
export const withFeedbackSourceRow = (
  rows: any[],
  endCallStatus: unknown,
  feedbackGeneratedAt?: unknown
): any[] => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;

  const key = typeof endCallStatus === "string" ? endCallStatus.trim() : "";
  const label = PROCTORING_FEEDBACK_SOURCE_LABELS[key];
  if (!label) return rows;

  // Idempotent: re-rendering the same list must not stack the row up.
  if (rows.some((message: any) => norm(message?.text) === label)) return rows;

  const lastRow = rows[rows.length - 1];
  const lastMs = new Date(lastRow?.dateAndTime).getTime();
  const generatedMs = new Date(feedbackGeneratedAt as string).getTime();

  // `> 0` is not redundant with the NaN check: `new Date(null)` is epoch 0, not NaN, and would print
  // "01 Jan 1970" on a client report. And the line can never sit above the row before it.
  const usable =
    !Number.isNaN(generatedMs) &&
    generatedMs > 0 &&
    (Number.isNaN(lastMs) || generatedMs >= lastMs);

  const dateAndTime = usable ? new Date(generatedMs).toISOString() : lastRow?.dateAndTime;

  return [
    ...rows,
    { type: "orange", text: label, dateAndTime, duration: "0", feedbackSource: true },
  ];
};

export const compareProctoringMessages = (a: any, b: any): number => {
  // Primary key: byte-for-byte the comparator that was inline in this file before this change.
  const timeA = new Date(a?.dateAndTime).getTime();
  const timeB = new Date(b?.dateAndTime).getTime();
  // [2026-09-16] CHANGED — INC-1003: compare by DISPLAYED minute, not by exact millisecond.
  // WHY: this view renders the time column with `toLocaleTimeString({hour, minute})` — seconds are
  // never shown. A real record (#CAN00670) stores the setup block as
  //   00:32:15.543 Interview Started · 00:32:16.543 Deepfake · 00:32:20.777 camera
  //   00:32:20.782 Fullscreen · 00:32:20.790 microphone
  // which every user sees as five rows all stamped "12:32 AM", in the wrong sequence. They are
  // milliseconds apart, so an exact-tie rule never fired and the spec order was never applied.
  // Because every row inside one displayed minute prints the SAME time string, reordering within
  // that minute cannot produce a visibly backwards clock — which is the property this fix must
  // never break. Rows in DIFFERENT minutes are still ordered strictly by time.
  const MINUTE_MS = 60 * 1000;
  const timeDiff = Math.floor(timeA / MINUTE_MS) - Math.floor(timeB / MINUTE_MS);
  // `timeDiff !== 0` is intentionally ALSO true when timeDiff is NaN (missing/invalid
  // dateAndTime), so NaN is returned exactly as the old comparator did — rows with broken
  // timestamps keep the position they have today. This fix must not start moving them.
  if (timeDiff !== 0) return timeDiff;
  // Reached only when both timestamps are valid AND exactly equal.
  const rankDiff = proctoringRowRank(a?.text) - proctoringRowRank(b?.text);
  if (rankDiff !== 0) return rankDiff;
  // Same displayed minute AND same rank (e.g. two "looking away" rows in one minute) — fall back
  // to true chronological order so monitoring events are never shuffled among themselves.
  return timeA - timeB;
};

const TruncatedText: React.FC<{ value: string; className?: string }> = ({ value, className }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (ref.current) {
      setIsTruncated(
        ref.current.scrollWidth > ref.current.clientWidth ||
          ref.current.scrollHeight > ref.current.clientHeight
      );
    }
  }, [value]);

  return (
    <p
      ref={ref}
      className={className}
      {...(isTruncated && {
        "data-tooltip-id": TOOLTIP_ID,
        "data-tooltip-content": value,
        "data-tooltip-place": "top",
      })}
    >
      {value}
    </p>
  );
};

// ─── ported from the dev line (helper.tsx, commit d0ee3fe) ───────────────────────────────
// [2026-09-18] ADDED — collapse an event that repeats inside one displayed minute.
//
// THE PROBLEM. The report renders hh:mm, but the detector emits one row per interval, so a single
// minute can carry several rows of the identical sentence:
//
//     10:34 am   Candidate looking away from screen for 24 seconds
//     10:34 am   Candidate looking away from screen for 30 seconds
//
// To a recruiter that reads as impossible — 54 seconds of looking away inside one minute, twice
// over — and a busy log runs to 18 of these. Measured read-only on dev: 730 such groups across the
// database, absorbing 1,747 rows.
//
// WHY SUMMING IS HONEST AND SPAN-MERGING IS NOT. 24s and 30s are two real, disjoint intervals, so
// "54 seconds" is the true total time the candidate was not looking at the screen. Nothing is
// invented. Merging by elapsed SPAN instead (first start → last end) would silently count the
// seconds in between, when the candidate WAS looking — an overstatement against the candidate on a
// document used to make a hiring decision. This function only ever adds durations.
//
// WHY THE COUNT IS PART OF THE SENTENCE. A bare "54 seconds" implies one unbroken stretch, which is
// the one thing that is not true. It also breaks down entirely on the 18.5% of groups whose total
// exceeds 60 seconds — the row time is the EVIDENCE-FRAME timestamp, not the interval start, so a
// long interval is labelled with a single minute even though it ran across two. "192 seconds" on a
// one-minute row reads more false than the problem being fixed. "4 times for 3 min 12 sec" cannot.
//
// SCORING IS UNTOUCHED, BY CONSTRUCTION. Every suspicious-activity rule reads the RAW api array —
// helper.tsx `apiFeedback?.proctoring?.messages` and `interview?.feedback?.proctoring?.messages`,
// and backend callEndService.ts counts the stored documents. This runs only on the rendered copy,
// and is strictly non-mutating (slice + spread, never an in-place write), so the arrays those rules
// read are the same objects they were before. Screenshots are a separate array and also untouched,
// so every piece of evidence survives.
const PROCTORING_COMBINE_FROM_MS = Date.parse("2026-09-18T00:00:00+05:30");

/** Which displayed minute a row falls in. NaN when the timestamp cannot be read. */
const proctoringDisplayMinute = (value: unknown): number => {
  const ms = new Date(value as string).getTime();
  return Number.isNaN(ms) ? Number.NaN : Math.floor(ms / 60000);
};

/** "54 seconds" / "1 min 22 sec" — minutes only once a bare second count stops being readable. */
const formatProctoringTotal = (totalSeconds: number): string => {
  if (totalSeconds < 60) return `${totalSeconds} seconds`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0
    ? `${minutes} min`
    : `${minutes} min ${String(seconds).padStart(2, "0")} sec`;
};

/**
 * Collapse consecutive rows that share BOTH the same text and the same displayed minute.
 *
 * A row of a different kind between two repeats breaks the group, deliberately: a
 * "Multiple faces detected" landing between two look-aways means they were not one run of the same
 * thing, and joining across it would reorder the reader's sense of what happened.
 *
 * @param rows  already ordered by `renderProctoringLog`
 * @param interviewDate  the interview's start; reports older than the cutoff are returned untouched
 */
export const combineRepeatedProctoringRows = (
  rows: any[] | undefined | null,
  interviewDate?: unknown
): any[] => {
  if (!Array.isArray(rows) || rows.length < 2) return Array.isArray(rows) ? rows : [];

  // Old reports keep exactly the log they have always shown. A recruiter who read a report last
  // week must not find a different one today — same reasoning as the header-date gate in
  // ProctoringLogsView.tsx. Only interviews from the cutoff onwards are combined.
  const startedMs = new Date(interviewDate as string).getTime();
  if (Number.isNaN(startedMs) || startedMs < PROCTORING_COMBINE_FROM_MS) return rows;

  const out: any[] = [];
  let index = 0;

  while (index < rows.length) {
    const first = rows[index];
    const text = norm(first?.text);
    const minute = proctoringDisplayMinute(first?.dateAndTime);

    // An unreadable timestamp or an empty text can never anchor a group — such a row passes
    // through alone rather than swallowing the row after it.
    let end = index + 1;
    if (text && Number.isFinite(minute)) {
      while (
        end < rows.length &&
        norm(rows[end]?.text) === text &&
        proctoringDisplayMinute(rows[end]?.dateAndTime) === minute
      ) {
        end += 1;
      }
    }

    const group = rows.slice(index, end);

    // The same event written twice is ONE event, not two: identical timestamp AND identical
    // duration means a retry or a regenerate, so summing it would double a real number. Rare —
    // one group in the whole dev database — but the one case where summing would lie.
    //
    // [2026-09-19] CHANGED — compare on the row's ORIGINAL timestamp when the clamp moved it.
    //
    // WHY: `clampRowsToProctoringWindow` folds every out-of-window row onto the SAME window edge.
    // Once it does, forty genuinely different events all carry one identical `dateAndTime`, and
    // this de-duplication — which was written for true retries — starts deleting real evidence
    // whenever two of them happen to share a duration.
    //
    // Measured on interview 6aad3fa52b672a460ddc5805, whose AI rows all landed 12h08m past the
    // interview end and were therefore all clamped to 12:01 pm:
    //     40 look-away events  ->  25 shown        (15 real events deleted)
    //     18 min 28 sec total  ->  16 min 06 sec   (142 seconds unaccounted for)
    // On a report marked "Not Recommended" that under-states the candidate's own evidence.
    //
    // `originalDateAndTime` is stamped by the clamp and only exists on rows it moved, so an
    // unclamped row falls straight through to the old key and the genuine-retry case is unchanged.
    // Two true duplicates that were BOTH clamped still share an original timestamp, so they are
    // still collapsed — the case this guard exists for keeps working.
    //
    // Old line kept for revert/compare:
    //   const key = `${new Date(row?.dateAndTime).getTime()}|${row?.duration}`;
    const seen = new Set<string>();
    const unique = group.filter((row: any) => {
      const key = `${new Date(row?.originalDateAndTime ?? row?.dateAndTime).getTime()}|${row?.duration}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (unique.length <= 1) {
      out.push(unique[0] ?? first);
    } else {
      const totalSeconds = unique.reduce(
        (sum: number, row: any) => sum + (parseInt(String(row?.duration ?? ""), 10) || 0),
        0
      );
      // `duration: "0"` so the view renders this text verbatim instead of appending its own
      // "for N seconds" — see ProctoringLogsView.tsx. The originals are kept on the row so the
      // combination stays auditable rather than being a number nobody can trace back.
      out.push({
        ...unique[0],
        // [2026-09-22] CHANGED — read the comma as "for". Asked for by Rujal on 2026-09-22.
        //
        // WHY: "11 times, 3 min 33 sec" is a list of two facts, and a reader has to guess how they
        // relate — is the second one the duration of the last event, or of all of them? "11 times
        // for 3 min 33 sec" says it: eleven events, three and a half minutes in total. Same numbers,
        // one less thing to work out. Text only — the count and the sum are unchanged, and nothing
        // that scores or stores reads this string.
        //
        // Old line kept for revert/compare:
        //   ? `${text} · ${unique.length} times, ${formatProctoringTotal(totalSeconds)}`
        text:
          totalSeconds > 0
            ? `${text} · ${unique.length} times for ${formatProctoringTotal(totalSeconds)}`
            : `${text} · ${unique.length} times`,
        duration: "0",
        combinedCount: unique.length,
        combinedDurationSec: totalSeconds,
        combinedFrom: unique.map((row: any) => ({
          dateAndTime: row?.dateAndTime,
          duration: row?.duration,
        })),
      });
    }

    index = end;
  }

  return out;
};

const ProctoringLogsView: React.FC = () => {
  const feedbackForm = useSelector(getFeedbackForm);
  const profileForm = useSelector(getProfileForm);
  const externalDevices = feedbackForm?.proctoringLogs?.externalDevices;

  const deviceItems = [
    {
      label: "Camera",
      value: externalDevices?.camera,
      Icon: CameraVideoIcon,
      type: "img",
    },
    {
      label: "Microphone",
      value: externalDevices?.mic,
      Icon: Mic,
      type: "icon",
    },
    {
      label: "Speaker",
      value: externalDevices?.speaker,
      Icon: Speaker,
      type: "icon",
    },
    {
      label: "External Monitor",
      value: externalDevices?.monitor,
      Icon: MonitorIcon,
      type: "img",
    },
    {
      label: "Keyboard",
      value: externalDevices?.keyboard,
      Icon: Keyboard,
      type: "icon",
    },
    {
      label: "Mouse",
      value: externalDevices?.mouse,
      Icon: MouseIcon,
      type: "img",
    },
    { label: "USB", value: externalDevices?.usb, Icon: UsbIcon, type: "img" },
  ].filter((item) => item.value);

  const hasNonEmptyText = (raw: unknown) => raw != null && String(raw).trim() !== "";

  // Map the proctoring logs to event logs format
  // [2026-09-16] CHANGED — INC-1003: the inline comparator is replaced by `orderProctoringLog`
  // above. It keeps `dateAndTime` as the primary key at displayed-minute precision, so the rendered
  // clock can never run backwards, and applies the spec setup order only inside a minute that
  // actually contains a session-start row. The filter and the `.map(...)` body are unchanged.
  // Old comparator kept below for revert/compare — restore these 3 lines to undo this change:
  //       ?.sort(
  //         (a: any, b: any) => new Date(a?.dateAndTime).getTime() - new Date(b?.dateAndTime).getTime()
  //       )
  // [2026-09-17] CHANGED — INC-1002/INC-1003: `renderProctoringLog` wraps `orderProctoringLog` and
  // additionally enforces the five display rules. Old call kept for revert/compare:
  //   orderProctoringLog(...)
  // [2026-09-17] ADDED — INC-1003: the separator decision is made HERE, against the last row that
  // actually had a usable day, not against the immediately preceding row. A row with an unparseable
  // `dateAndTime` has an empty `dayKey`; comparing the next row against that empty value printed a
  // date separator in the middle of a single-day interview.
  let lastRenderedDayKey = "";
  // [2026-09-18] CHANGED — collapse an event that repeats inside one displayed minute, so a single
  // minute stops carrying several rows of the identical sentence ("…for 24 seconds" then "…for 30
  // seconds" both at 10:34). Durations are summed and the count is written into the row, never a
  // merge by elapsed span — see combineRepeatedProctoringRows above for why that matters.
  // Gated on the interview date, so reports a client has already read are returned untouched.
  // The suspicious-activity rating is unaffected: it reads the raw api array, not this copy.
  // Old expression kept below, commented out.
  //
  //   renderProctoringLog(
  //     feedbackForm?.proctoringLogs?.messages?.filter((message: any) =>
  //       hasNonEmptyText(message?.text)
  //     )
  //   )?.map((message: any) => {
  // [2026-09-18] Both wrappers apply, in this order deliberately: collapse the repeats FIRST, then
  // append the feedback-source line. That line belongs at the very bottom, below "Interview Ended",
  // so it must be added after the log is otherwise final.
  const eventLogs =
    withFeedbackSourceRow(
      combineRepeatedProctoringRows(
        renderProctoringLog(
          feedbackForm?.proctoringLogs?.messages?.filter((message: any) =>
            hasNonEmptyText(message?.text)
          )
        ),
        profileForm?.interviewDate
      ),
      profileForm?.endCallStatus,
      profileForm?.interviewEndDate
    )?.map((message: any) => {
      // Convert ISO date to readable time format
      const date = new Date(message.dateAndTime);
      const time = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      // [2026-09-17] ADDED — INC-1003: carry the row's calendar DAY so a log that spans more than
      // one day can print a separator. Until now a single header was rendered from
      // `profileForm.interviewDate` and every row showed hour+minute only, so rows written on a
      // later day stacked under the first day's heading and the clock appeared to run backwards —
      // e.g. a real record reading 06:11 pm (Sep 13), 08:11 am (Sep 14), 09:55 am (Sep 17).
      const dayKey = Number.isNaN(date.getTime()) ? "" : date.toDateString();

      // Map message type to EventLogItem type
      const typeMap: Record<string, "success" | "warning" | "error"> = {
        green: "success",
        orange: "warning",
        red: "error",
      };

      // Create event text with duration if available
      const eventText =
        message.duration && message.duration !== "0"
          ? `${message.text} for ${message.duration} seconds`
          : message.text;

      const showDaySeparator =
        Boolean(dayKey) && lastRenderedDayKey !== "" && dayKey !== lastRenderedDayKey;

      if (dayKey) lastRenderedDayKey = dayKey;

      return {
        time,
        showDaySeparator,
        dayLabel: dayKey ? formatDate(message.dateAndTime, 1) : "",
        event: eventText,
        type: typeMap[message.type] || "error",
      };
    }) || [];

  // [2026-09-17] ADDED — INC-1003: the header above this log must be the day the log actually
  // STARTS on, not the interview's scheduled start.
  //
  // `profileForm.interviewDate` is mapped from `interviewStartDate` (helper.tsx:356), and that field
  // is rewritten every time a candidate resumes — so for a resumed interview it holds the RESUME
  // time. The first rendered row never prints a separator (there is no previous day to differ
  // from), so when those two land on different days the header states a date that no row below it
  // belongs to. Measured read-only against dev on 2026-09-17: 27 of 3,206 logs (0.84%), 26 of them
  // multi-day; the worst reads "Fri May 03 2024" above a first row written on "Thu Oct 26 2023".
  //
  // Taking the first dated row's own label removes the contradiction and changes nothing for the
  // other 3,170 logs, where the two already agree. `profileForm.interviewDate` remains the fallback
  // for a log with no readable row at all, so an empty log still prints exactly what it printed
  // before. Old line kept for revert/compare:
  //   {formatDate(profileForm.interviewDate, 1)}
  //
  // [2026-09-17] CHANGED — INC-1003: NEW REPORTS ONLY. A report that already exists keeps the
  // header it has always had, right or wrong. This is the only change in this ticket that can alter
  // how an already-stored report LOOKS — the backend end-row fix cannot, because `callEndFn` only
  // ever appends to an interview whose `interviewEndDate` does not exist yet — so it is the only one
  // that needs a cutoff. A client who printed or downloaded a report last month must not open it
  // today and find a different date at the top; a wrong date they have already seen is less harmful
  // than a date that changed underneath them.
  //
  // The cutoff is the first midnight IST after this ships. A plain constant with the reasoning
  // beside it, not an environment variable, per rule.md. `interviewStartDate` is the comparison
  // because it is the same value the header falls back to; an interview that cannot supply a
  // readable one keeps the old header, which is the safe direction.
  //
  // Deliberately declared here and in cefrFeedback/views/ProctoringLogsView.tsx rather than exported
  // from helper.tsx: those two components are already parallel copies of each other, and this keeps
  // helper.tsx — where the display rules agreed on 2026-09-17 live — untouched. If the date is ever
  // moved, both copies must move together.
  // [2026-09-22] CHANGED — the cutoff above is REMOVED. Raised by #CAN00252, whose log reads
  //     Sep 9, 2026  ->  Sep 7, 2026  ->  Sep 9, 2026
  // top to bottom. The interview really ran across Sep 4, Sep 7 and Sep 9 (three beam recordings,
  // and the first evidence screenshot is captioned "Sep 4, 2026 09:15 am"), so the header is a day
  // the rows beneath it do not belong to, sitting above separators that say so.
  //
  // WHY THE CUTOFF'S OWN ARGUMENT NO LONGER HOLDS. It was added so a client who had already seen a
  // report would not find a different date at the top. That reasoning assumed the rest of the
  // report stayed still — and it did not: the day SEPARATOR shipped in the same ticket with no gate
  // at all (see `showDaySeparator` above, computed unconditionally). So an already-stored multi-day
  // report changed anyway, gaining correct separators while keeping the old header. The gate did
  // not preserve the old report; it preserved only the half of it that was wrong, and turned a
  // merely-inaccurate header into one that contradicts the body directly below it.
  //
  // Measured on the real export of 3,245 logs: 115 (3.54%) span more than one day and therefore now
  // render separators, the widest across 698 days. Of those, the ~27 (0.84%) whose stored
  // `interviewStartDate` falls on a later day than their first row are the ones reading like
  // #CAN00252 today. Every other report is unaffected — where the two dates already agree, taking
  // the first row's own label produces the identical string.
  //
  // The fallback is unchanged, so a log with no readable row at all still renders exactly what it
  // rendered before.
  //
  // Old lines kept for revert/compare:
  //   const PROCTORING_HEADER_FIX_FROM_MS = Date.parse("2026-09-18T00:00:00+05:30");
  //   const interviewStartedMs = new Date(profileForm.interviewDate).getTime();
  //   const isNewReport =
  //     !Number.isNaN(interviewStartedMs) && interviewStartedMs >= PROCTORING_HEADER_FIX_FROM_MS;
  //   const proctoringHeaderDate =
  //     isNewReport && firstDatedLog?.dayLabel
  //       ? firstDatedLog.dayLabel
  //       : formatDate(profileForm.interviewDate, 1);
  const firstDatedLog = eventLogs.find((log: { dayLabel: string }) => Boolean(log.dayLabel));
  const proctoringHeaderDate = firstDatedLog?.dayLabel
    ? firstDatedLog.dayLabel
    : formatDate(profileForm.interviewDate, 1);

  // [2026-09-22] ADDED — group the rows into one block per day, so each day can carry its own
  // label and its own timeline rule. See the render below for why.
  //
  // `showDaySeparator` is reused as the boundary rather than recomputing the day: it was already
  // derived from each row's `toDateString()` a few lines above, and deriving it twice is how the
  // two would eventually disagree. A row whose timestamp cannot be read carries `showDaySeparator:
  // false`, so it stays in the block it is already sitting in instead of starting a new one —
  // which is the same thing the old inline separator did with it.
  type ProctoringDayLog = {
    time: string;
    showDaySeparator: boolean;
    dayLabel: string;
    event: string;
    type: "success" | "warning" | "error";
  };
  const proctoringDayBlocks = eventLogs.reduce(
    (blocks: { label: string; logs: ProctoringDayLog[] }[], log: ProctoringDayLog) => {
      if (blocks.length === 0 || log.showDaySeparator) {
        blocks.push({ label: log.dayLabel, logs: [log] });
      } else {
        blocks[blocks.length - 1].logs.push(log);
      }
      return blocks;
    },
    []
  );

  // Map the screenshots data
  //
  // [2026-09-19] CHANGED — clamp the evidence images to the SAME interview window as the log above.
  //
  // The captions under these images render from `feedback.proctoring.screenshots`, a different
  // array from the one the log rows come from, and it never passed through the clamp. So the two
  // panels disagreed about the same event: the log row read "12:01 pm" while the caption beneath
  // its own screenshot read "Sep 20, 2026 12:10 am" (interview 6aad3fa52b672a460ddc5805).
  //
  // Both values come from one backend write — interviewService.ts:331 stamps the log row and :347
  // stamps the screenshot, both from `evidenceDateAndTime`. They are the same fact, so they must
  // print the same time.
  //
  // The window is derived from the RAW message array, not from `eventLogs`: `eventLogs` has already
  // been clamped, combined and had the feedback-source row appended, so reading the window back out
  // of it would be circular. `clampProctoringEvidenceToInterviewWindow` returns its input untouched
  // when there is no usable window, so a report that is not clamped today does not start being one.
  //
  // Old line kept for revert/compare:
  //   const screenshots = feedbackForm?.proctoringLogs?.screenshots
  const screenshots =
    clampProctoringEvidenceToInterviewWindow(
      feedbackForm?.proctoringLogs?.screenshots,
      feedbackForm?.proctoringLogs?.messages
    )
      ?.sort(
        (a: any, b: any) => new Date(a?.dateAndTime).getTime() - new Date(b?.dateAndTime).getTime()
      )
      ?.map((screenshot: any, index: number) => {
        const dateObj = new Date(screenshot.dateAndTime);

        // Format like: Feb 4, 2026
        const formattedDate = dateObj.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });

        // Format like: 02:27 pm
        const formattedTime = dateObj
          .toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          .toLowerCase(); // makes AM/PM → am/pm

        return {
          title: `Screenshot #${index + 1}`,
          timestamp: `${formattedDate} ${formattedTime}`, // same as image (two lines)
          description:
            screenshot.duration && screenshot.duration !== "0"
              ? `${screenshot.text} for ${screenshot.duration} seconds`
              : screenshot.text,
          imageUrl: screenshot.url,
          type: screenshot.type,
        };
      }) || [];

  return (
    <div className="p-3 md:p-6">
      <div className="md:hidden print:flex w-100 p-1 print:p-2 rounded-md border-b-4 border-teal-500 bg-teal-50 text-teal-500 text-md print:text-xl flex justify-center font-semibold mb-3 md:mb-6">
        Proctoring Logs
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-8">
        {/* Proctoring Events Log */}
        <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-2 mb-6">
            <Monitor className="w-5 h-5 text-gray-600" />
            <h2 className="text-md md:text-lg font-semibold text-gray-900">
              Proctoring Events Log
            </h2>
          </div>

          {/* [2026-09-22] CHANGED — one BLOCK per day. Asked for by Rujal on 2026-09-22.
              Layout only: the rows, their order, their text and their times are untouched.

              BEFORE, the blue timeline was a single `border-l-2` wrapping every row, and a new
              day was a bare text line INSIDE it. That put the date at the same indent as the time
              column rather than beside the events, and left a stretch of line carrying no dot,
              which reads as a broken timeline rather than a new day (#CAN00252).

              NOW each day owns its label and its own line, so the break between days is
              deliberate and looks it. The first block needs no separate header — it IS the
              header — so `proctoringHeaderDate` is used as its label, which also keeps that
              value's fallback for a log whose rows carry no readable date.

              Old block:
              <div className="space-y-1">
                <div className="text-xs md:text-sm font-medium text-gray-700 mb-2 md:mb-4">
                  {proctoringHeaderDate}
                </div>
                <div className="border-l-2 border-blue-500 pl-4 md:space-y-1">
                  {eventLogs.map((log, index) => (
                    <React.Fragment key={index}>
                      {log.showDaySeparator && (
                        <div className="text-xs md:text-sm font-medium text-gray-700 mt-3 mb-2 md:mt-4 md:mb-4">
                          {log.dayLabel}
                        </div>
                      )}
                      <EventLogItem time={log.time} event={log.event} type={log.type} />
                    </React.Fragment>
                  ))}
                </div>
              </div> */}
          {/* [2026-09-22] CHANGED — `space-y-4`, and the per-block `mt-5 md:mt-8` dropped.
              Tailwind emits space-y as `.space-y-1 > :not([hidden]) ~ :not([hidden])`, specificity
              (0,3,0), against `.mt-5` at (0,1,0) — so the container was WINNING and every gap in
              this list was 0.25rem no matter what the label asked for. The extra room between days
              was never applied; that is why the blocks read as cramped. One honest value on the
              container replaces a margin that could not take effect.
              Old lines kept for revert/compare:
                <div className="space-y-1">
                  ... className={`... mb-2 md:mb-4 ${blockIndex > 0 ? "mt-5 md:mt-8" : ""}`} */}
          <div className="space-y-4">
            {proctoringDayBlocks.map((block, blockIndex: number) => (
              <React.Fragment key={blockIndex}>
                <div className="text-xs md:text-sm font-medium text-gray-700 mb-2 md:mb-4">
                  {blockIndex === 0 ? proctoringHeaderDate : block.label}
                </div>
                <div className="border-l-2 border-blue-500 pl-4 md:space-y-1">
                  {block.logs.map((log, index: number) => (
                    <EventLogItem
                      key={index}
                      time={log.time}
                      event={log.event}
                      type={log.type}
                    />
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Right column: External Device Information + Screenshots */}
        <div className="flex flex-col gap-3 md:gap-8">
          {/* External Device Information */}
          {deviceItems.length > 0 && (
            <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-2 mb-4 md:mb-6">
                <div className="p-1.5 bg-[#EEF2FF] rounded-md">
                  <Monitor className="w-4 h-4 md:w-5 md:h-5 text-[#133F7D]" />
                </div>
                <h2 className="text-md md:text-lg font-semibold text-gray-900">
                  Device Information
                </h2>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                {deviceItems.map(({ label, value, Icon, type }) => (
                  <div key={label} className="flex items-start space-x-3">
                    <div className="p-2 bg-indigo-50 rounded-lg flex-shrink-0">
                      {type !== "img" ? (
                        <Icon className="w-4 h-4 text-[#133F7D]" />
                      ) : (
                        <img
                          src={Icon as unknown as string}
                          className="w-4 h-4 text-[#133F7D]"
                          alt="monitor-icon"
                        />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] text-[#65758B] font-medium">{label}</p>
                      <TruncatedText
                        value={value}
                        className="text-[11px] font-normal text-[#14181F] truncate"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <Tooltip
                id={TOOLTIP_ID}
                className="!z-50 !max-w-[220px] !text-[10px] !leading-4 !whitespace-normal !break-words"
              />
            </div>
          )}

          {/* Proctoring Events Screenshots */}
          {screenshots && screenshots.length > 0 && (
            <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-2 mb-3 md:mb-6">
                <Camera className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                <h2 className="text-md md:text-lg font-semibold text-gray-900">
                  Proctoring Events Screenshots
                </h2>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 md:gap-4">
                {screenshots.map(
                  (
                    screenshot: {
                      title: string;
                      timestamp: string;
                      description: string;
                      imageUrl: string;
                      type: string;
                    },
                    index: number
                  ) => (
                    <ScreenshotCard
                      key={index}
                      title={screenshot.title}
                      timestamp={screenshot.timestamp}
                      description={screenshot.description}
                      imageUrl={screenshot.imageUrl}
                      type={screenshot.type as "green" | "orange" | "red"}
                    />
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProctoringLogsView;
